import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CoachingHint, QuizQuestion } from '@/services/ai.service';
import { SecurityGap } from '@/services/assessment.service';

export interface QuizAttempt {
  questionId: string;
  answer: number;
  correct: boolean;
  timestamp: number;
}

// Multi-question quiz from backend
export interface MultiQuestionQuiz {
  id: string;
  questions: Array<{
    gapId: string;
    gapTitle: string;
    question: string;
    choices: Array<{ id: string; text: string; isCorrect: boolean }>;
    hint: string;
    explanation: string;
  }>;
  currentIndex: number;
  metadata: {
    totalPoints: number;
    pointsPerQuestion: number;
  };
}

export interface CoachingState {
  hints: CoachingHint[];
  progressFeedback: string;
  isExpanded: boolean;
  dismissedHints: string[];
  completedQuizzes: string[];
  quiz: {
    attempts: QuizAttempt[];
    showHint: boolean;
    isModalOpen: boolean;
    activeQuiz?: QuizQuestion;
    // Multi-question quiz support
    multiQuiz?: MultiQuestionQuiz;
    currentQuestionIndex: number;
    pendingGap?: SecurityGap;
    lastFeedback?: string;
    currentAttempts: number;
    gapId?: string;
  };
  totalPoints: number;
  discoveredGaps: string[];
  completionPercentage: number;
  // Course context for course-scoped progress
  activeCourseId: string | null;
  activeCohortId: string | null;
}

const initialState: CoachingState = {
  hints: [],
  progressFeedback: '',
  isExpanded: true,
  dismissedHints: [],
  completedQuizzes: [],
  quiz: {
    attempts: [],
    showHint: false,
    isModalOpen: false,
    activeQuiz: undefined,
    multiQuiz: undefined,
    currentQuestionIndex: 0,
    pendingGap: undefined,
    lastFeedback: undefined,
    currentAttempts: 0,
    gapId: undefined
  },
  totalPoints: 0,
  discoveredGaps: [],
  completionPercentage: 0,
  activeCourseId: null,
  activeCohortId: null,
};

const coachingSlice = createSlice({
  name: 'coaching',
  initialState,
  reducers: {
    hydrateCoaching: (state, action: PayloadAction<Partial<Pick<CoachingState, 'totalPoints' | 'completedQuizzes' | 'discoveredGaps' | 'activeCourseId' | 'activeCohortId'>>>) => {
      const { totalPoints, completedQuizzes, discoveredGaps, activeCourseId, activeCohortId } = action.payload;
      if (typeof totalPoints === 'number' && !Number.isNaN(totalPoints)) {
        state.totalPoints = totalPoints;
      }
      if (Array.isArray(completedQuizzes)) {
        state.completedQuizzes = completedQuizzes;
      }
      if (Array.isArray(discoveredGaps)) {
        state.discoveredGaps = discoveredGaps;
      }
      if (activeCourseId !== undefined) {
        state.activeCourseId = activeCourseId;
      }
      if (activeCohortId !== undefined) {
        state.activeCohortId = activeCohortId;
      }
    },
    setActiveCourse: (state, action: PayloadAction<{ courseId: string | null; cohortId: string | null }>) => {
      state.activeCourseId = action.payload.courseId;
      state.activeCohortId = action.payload.cohortId;
    },
    setHints: (state, action: PayloadAction<CoachingHint[]>) => {
      state.hints = action.payload;
    },
    addHint: (state, action: PayloadAction<CoachingHint>) => {
      state.hints.push(action.payload);
    },
    dismissHint: (state, action: PayloadAction<string>) => {
      state.dismissedHints.push(action.payload);
      state.hints = state.hints.filter(hint => hint.id !== action.payload);
    },
    setProgressFeedback: (state, action: PayloadAction<string>) => {
      state.progressFeedback = action.payload;
    },
    toggleExpanded: (state) => {
      state.isExpanded = !state.isExpanded;
    },
    setExpanded: (state, action: PayloadAction<boolean>) => {
      state.isExpanded = action.payload;
    },
    setPendingGap: (state, action: PayloadAction<SecurityGap>) => {
      state.quiz.pendingGap = action.payload;
    },
    gapDiscovered: (state, action: PayloadAction<{ gap: SecurityGap; quiz: QuizQuestion }>) => {
      state.discoveredGaps.push(action.payload.gap.id);
      state.quiz.pendingGap = action.payload.gap;
      state.quiz.gapId = action.payload.gap.id;
      // Open quiz modal with the generated quiz
      state.quiz.isModalOpen = true;
      state.quiz.activeQuiz = action.payload.quiz;
      state.quiz.showHint = false;
      state.quiz.currentAttempts = 0;
      state.quiz.lastFeedback = undefined;
      // Add coaching hint about gap discovery
      state.progressFeedback = `You've identified a security gap. Complete the knowledge check to validate your finding.`;
      // Expand the coaching panel to show the quiz
      state.isExpanded = true;
    },
    openQuizModal: (state, action: PayloadAction<QuizQuestion>) => {
      state.quiz.isModalOpen = true;
      state.quiz.activeQuiz = action.payload;
      state.quiz.showHint = false;
      state.quiz.currentAttempts = 0;
      state.quiz.lastFeedback = undefined;
      // Expand the coaching panel to show the quiz
      state.isExpanded = true;
    },
    closeQuizModal: (state) => {
      state.quiz.isModalOpen = false;
      state.quiz.activeQuiz = undefined;
      state.quiz.multiQuiz = undefined;
      state.quiz.currentQuestionIndex = 0;
      state.quiz.showHint = false;
      state.quiz.currentAttempts = 0;
      state.quiz.lastFeedback = undefined;
    },
    // Open multi-question quiz from backend
    openMultiQuiz: (state, action: PayloadAction<MultiQuestionQuiz>) => {
      state.quiz.isModalOpen = true;
      state.quiz.multiQuiz = action.payload;
      state.quiz.currentQuestionIndex = 0;
      state.quiz.showHint = false;
      state.quiz.currentAttempts = 0;
      state.quiz.lastFeedback = undefined;
      // Set gapId to current question's gap
      if (action.payload.questions.length > 0) {
        state.quiz.gapId = action.payload.questions[0].gapId;
      }
      state.isExpanded = true;
    },
    // Advance to next question in multi-quiz
    advanceToNextQuestion: (state) => {
      if (state.quiz.multiQuiz && state.quiz.currentQuestionIndex < state.quiz.multiQuiz.questions.length - 1) {
        state.quiz.currentQuestionIndex += 1;
        state.quiz.showHint = false;
        state.quiz.currentAttempts = 0;
        state.quiz.lastFeedback = undefined;
        // Update gapId to current question's gap
        state.quiz.gapId = state.quiz.multiQuiz.questions[state.quiz.currentQuestionIndex].gapId;
      } else {
        // No more questions, close the modal
        state.quiz.isModalOpen = false;
        state.quiz.multiQuiz = undefined;
        state.quiz.currentQuestionIndex = 0;
        state.quiz.showHint = false;
        state.quiz.currentAttempts = 0;
        state.quiz.lastFeedback = undefined;
      }
    },
    addQuizAttempt: (state, action: PayloadAction<QuizAttempt>) => {
      state.quiz.attempts.push(action.payload);
      state.quiz.currentAttempts += 1;

      if (action.payload.correct) {
        state.completedQuizzes.push(action.payload.questionId);
        // NOTE: do NOT add to totalPoints here. Points are server-authoritative and are synced by
        // BOTH quiz components via hydrateCoaching({ totalPoints: result.totalPoints }) right around
        // this dispatch. A client-side `state.totalPoints += pointsEarned` here double-counted every
        // correct answer (QuizSection sets the server total, then this reducer added it again →
        // 15→30→45 instead of 15→30). The server total is the single source of truth.
      }
    },
    setQuizFeedback: (state, action: PayloadAction<string>) => {
      state.quiz.lastFeedback = action.payload;
    },
    showQuizHint: (state) => {
      state.quiz.showHint = true;
    },
    resetCoaching: () => {
      return initialState;
    },
    // Update progress from backend CoachingAgentService response
    updateProgress: (state, action: PayloadAction<{
      totalPoints: number;
      discoveredGaps: string[];
      completionPercentage: number;
      newGapId?: string;
    }>) => {
      const { totalPoints, discoveredGaps, completionPercentage, newGapId } = action.payload;
      state.totalPoints = totalPoints;
      state.discoveredGaps = discoveredGaps;
      state.completionPercentage = completionPercentage;
      if (newGapId) {
        state.progressFeedback = `Progress: ${completionPercentage}% complete!`;
      }
    }
  }
});

export const {
  hydrateCoaching,
  setActiveCourse,
  setHints,
  addHint,
  dismissHint,
  setProgressFeedback,
  toggleExpanded,
  setExpanded,
  setPendingGap,
  gapDiscovered,
  openQuizModal,
  closeQuizModal,
  openMultiQuiz,
  advanceToNextQuestion,
  addQuizAttempt,
  setQuizFeedback,
  showQuizHint,
  resetCoaching,
  updateProgress
} = coachingSlice.actions;

export default coachingSlice.reducer;
